import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";

import {
  fetchEventByCode,
  fetchParticipantsByEvent,
  fetchRemindersByEvent,
  fetchMoMByEvent,
} from "../Api/ViewEvent";

import {
  updateEvent,
  addParticipants,
  addReminder,
  addMoM,
} from "../Api/Event";

import "./EditEvent.css";

const EditEvent = () => {
  const { eventCode } = useParams();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = userDetails?.organisationId;
  const userId = userDetails?.userId;

  const [eventData, setEventData] = useState(null);

  const [reminderType, setReminderType] = useState("");
  const [isSent, setIsSent] = useState(false);

  const [existingReminders, setExistingReminders] = useState([]);
  const [existingMoM, setExistingMoM] = useState(null);

  const [existingParticipants, setExistingParticipants] = useState([]);

  const [participants, setParticipants] = useState([]);
  const [reminderMinutes, setReminderMinutes] = useState("");
  const [mom, setMom] = useState({
    discussionPoints: "",
    decisionsTaken: "",
    actionItems: "",
  });

  useEffect(() => {
    loadEvent();
  }, []);

  const loadEvent = async () => {
    try {
      const data = await fetchEventByCode(orgId, eventCode);

      setEventData({
        eventName: data.eventName,
        eventType: data.eventType,
        eventDate: moment(data.eventDate).format("YYYY-MM-DD"),
        eventEndDate: data.eventEndDate
          ? moment(data.eventEndDate).format("YYYY-MM-DD")
          : "",
        eventLocation: data.eventLocation,
        organizerName: data.organizerName,
        budgetAllocation: data.budgetAllocation,
        department: data.department,
        eventDescription: data.eventDescription,
        entityType: data.entityType,
        entityId: data.entityId,
      });
    } catch (error) {
      toast.error("Error loading event");
    }
  };

  const handleChange = (e) => {
    setEventData({
      ...eventData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOverviewSave = async () => {
    try {
      const payload = {
        ...eventData,
        eventDate: new Date(eventData.eventDate).toISOString(),
        eventEndDate: eventData.eventEndDate
          ? new Date(eventData.eventEndDate).toISOString()
          : null,
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await updateEvent(eventCode, payload);

      toast.success("Event Updated Successfully");
    } catch (error) {
      toast.error("Failed to update event");
    }
  };

  const handleAddParticipant = async () => {
    try {
      const payload = participants.map((p) => ({
        ...p,
        createdBy: String(userId),
        organisationId: String(orgId),
      }));

      await addParticipants(eventCode, payload);

      toast.success("Participants Added");

      setParticipants([]);

      loadParticipants();
    } catch (error) {
      toast.error("Error adding participants");
    }
  };

  const handleAddReminder = async () => {
    try {
      const payload = {
        reminderMinutes: reminderMinutes,
        reminderType: reminderType,
        isSent: isSent === "true",
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await addReminder(eventCode, payload);

      toast.success("Reminder Saved");

      setReminderMinutes("");
      setReminderType("");
      setIsSent(false);

      loadReminders();
    } catch {
      toast.error("Error saving reminder");
    }
  };

  const handleSaveMoM = async () => {
    try {
      const payload = {
        ...mom,
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await addMoM(eventCode, payload);

      toast.success("Minutes of Meeting Saved");

      loadMoM();
    } catch {
      toast.error("Error saving MoM");
    }
  };

  const loadParticipants = async () => {
    try {
      const data = await fetchParticipantsByEvent(eventCode);
      setExistingParticipants(data);
    } catch (error) {
      toast.error("Error loading participants");
    }
  };

  const loadReminders = async () => {
    try {
      const data = await fetchRemindersByEvent(eventCode);
      setExistingReminders(data);
    } catch {
      toast.error("Error loading reminders");
    }
  };

  const loadMoM = async () => {
    try {
      const data = await fetchMoMByEvent(eventCode);
      setExistingMoM(data);
    } catch {
      toast.error("Error loading MoM");
    }
  };

  useEffect(() => {
    loadEvent();
    loadParticipants();
    loadReminders();
    loadMoM();
  }, []);

  if (!eventData) return null;

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h2 className="fields-title">
          {eventData?.eventName} ({eventCode})
        </h2>
        <Tabs defaultActiveKey="overview" className="event-tabs">
          {/* OVERVIEW TAB */}

          <Tab eventKey="overview" title="📋 Overview">
            <Card className="mt-3">
              <Card.Body>
                <Form className="fields-form">
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event Name</Form.Label>
                        <Form.Control
                          name="eventName"
                          value={eventData.eventName}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event Type</Form.Label>
                        <Form.Control
                          name="eventType"
                          value={eventData.eventType}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="eventDate"
                          value={eventData.eventDate}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event End Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="eventEndDate"
                          value={eventData.eventEndDate}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event Location</Form.Label>
                        <Form.Control
                          name="eventLocation"
                          value={eventData.eventLocation}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Organizer</Form.Label>
                        <Form.Control
                          name="organizerName"
                          value={eventData.organizerName}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Budget Allocation</Form.Label>
                        <Form.Control
                          type="number"
                          name="budgetAllocation"
                          value={eventData.budgetAllocation}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Department</Form.Label>
                        <Form.Control
                          name="department"
                          value={eventData.department}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="eventDescription"
                          value={eventData.eventDescription}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Entity Type</Form.Label>
                        <Form.Select
                          name="entityType"
                          value={eventData.entityType}
                          onChange={handleChange}
                        >
                          <option value="">Select</option>
                          <option value="Ticket">Ticket</option>
                          <option value="Project">Project</option>
                          <option value="Task">Task</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Entity Id</Form.Label>
                        <Form.Control
                          name="entityId"
                          value={eventData.entityId}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="fields-btn-container">
                    <Button
                      variant="primary"
                      className="fields-submit"
                      onClick={handleOverviewSave}
                    >
                      Update Event
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Tab>

          {/* PARTICIPANTS */}

          <Tab eventKey="participants" title="👥 Participants">
            <Card className="mt-3">
              <Card.Body>
                <Form className="fields-form">
                  {participants.map((p, i) => (
                    <Row key={i} className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>User Id</Form.Label>
                          <Form.Control
                            value={p.userId}
                            onChange={(e) => {
                              const arr = [...participants];
                              arr[i].userId = e.target.value;
                              setParticipants(arr);
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Participant Role</Form.Label>
                          <Form.Control
                            value={p.participantRole}
                            onChange={(e) => {
                              const arr = [...participants];
                              arr[i].participantRole = e.target.value;
                              setParticipants(arr);
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            value={p.status}
                            onChange={(e) => {
                              const arr = [...participants];
                              arr[i].status = e.target.value;
                              setParticipants(arr);
                            }}
                          >
                            <option value="">Select</option>
                            <option value="Accepted">Accepted</option>
                            <option value="Pending">Pending</option>
                            <option value="Declined">Declined</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  ))}

                  <div className="fields-btn-container">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setParticipants([
                          ...participants,
                          { userId: "", participantRole: "", status: "" },
                        ])
                      }
                    >
                      Add Participant
                    </Button>

                    <Button
                      variant="primary"
                      className="fields-submit ms-2"
                      onClick={handleAddParticipant}
                    >
                      Save Participants
                    </Button>
                  </div>
                </Form>

                {/* EXISTING PARTICIPANTS TABLE */}

                {existingParticipants.length > 0 && (
                  <>
                    <hr />

                    <h5>Existing Participants</h5>

                    <Table bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User Id</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Created By</th>
                          <th>Date</th>
                        </tr>
                      </thead>

                      <tbody>
                        {existingParticipants.map((p, index) => (
                          <tr key={p.participantGuid}>
                            <td>{index + 1}</td>
                            <td>{p.userId}</td>
                            <td>{p.participantRole}</td>
                            <td>{p.status}</td>
                            <td>{p.createdBy}</td>
                            <td>
                              {moment(p.createdOnDate).format("DD-MM-YYYY")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* REMINDERS */}

          <Tab eventKey="reminders" title="⏰ Reminders">
            <Card className="mt-3">
              <Card.Body>
                <Form className="fields-form">
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reminder Minutes</Form.Label>
                        <Form.Control
                          type="number"
                          value={reminderMinutes}
                          onChange={(e) => setReminderMinutes(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reminder Type</Form.Label>
                        <Form.Select
                          value={reminderType}
                          onChange={(e) => setReminderType(e.target.value)}
                        >
                          <option value="">Select</option>
                          <option value="Email">Email</option>
                          <option value="Notification">Notification</option>
                          <option value="SMS">SMS</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Is Sent</Form.Label>
                        <Form.Select
                          value={isSent}
                          onChange={(e) => setIsSent(e.target.value)}
                        >
                          <option value="">Select</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="fields-btn-container">
                    <Button
                      variant="primary"
                      className="fields-submit"
                      onClick={handleAddReminder}
                    >
                      Save Reminder
                    </Button>
                  </div>
                </Form>
                {existingReminders.length > 0 && (
                  <>
                    <hr />
                    <h5>Existing Reminders</h5>

                    <Table bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Minutes</th>
                          <th>Type</th>
                          <th>Sent</th>
                          <th>Created By</th>
                          <th>Date</th>
                        </tr>
                      </thead>

                      <tbody>
                        {existingReminders.map((r, index) => (
                          <tr key={r.reminderGuid}>
                            <td>{index + 1}</td>
                            <td>{r.reminderMinutes}</td>
                            <td>{r.reminderType}</td>
                            <td>{r.isSent ? "Yes" : "No"}</td>
                            <td>{r.createdBy}</td>
                            <td>
                              {moment(r.createdOnDate).format("DD-MM-YYYY")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* MOM */}

          <Tab eventKey="mom" title="📝 MoM">
            <Card className="mt-3">
              <Card.Body>
                <Form className="fields-form">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prepared By</Form.Label>
                        <Form.Control
                          onChange={(e) =>
                            setMom({ ...mom, preparedBy: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Approved By</Form.Label>
                        <Form.Control
                          onChange={(e) =>
                            setMom({ ...mom, approvedBy: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Discussion Points</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          onChange={(e) =>
                            setMom({ ...mom, discussionPoints: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Decisions Taken</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          onChange={(e) =>
                            setMom({ ...mom, decisionsTaken: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Action Items</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          onChange={(e) =>
                            setMom({ ...mom, actionItems: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="fields-btn-container">
                    <Button
                      variant="primary"
                      className="fields-submit"
                      onClick={handleSaveMoM}
                    >
                      Save MoM
                    </Button>
                  </div>
                </Form>
                {existingMoM && (
                  <>
                    <hr />
                    <h5>Saved MoM</h5>

                    <Table bordered>
                      <tbody>
                        <tr>
                          <td>
                            <b>Prepared By</b>
                          </td>
                          <td>{existingMoM.preparedBy}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Approved By</b>
                          </td>
                          <td>{existingMoM.approvedBy}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Discussion Points</b>
                          </td>
                          <td>{existingMoM.discussionPoints}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Decisions Taken</b>
                          </td>
                          <td>{existingMoM.decisionsTaken}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Action Items</b>
                          </td>
                          <td>{existingMoM.actionItems}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
};

export default EditEvent;

// import { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import {
//   Container,
//   Card,
//   Tabs,
//   Tab,
//   Row,
//   Col,
//   Form,
//   Button,
//   Table,
// } from "react-bootstrap";
// import moment from "moment";
// import { toast } from "react-toastify";

// import { fetchEventByCode } from "../Api/ViewEvent";
// import {
//   updateEvent,
//   addParticipants,
//   addReminder,
//   addMoM,
// } from "../Api/Event";

// const EditEvent = () => {
//   const { eventCode } = useParams();

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const orgId = userDetails?.organisationId;

//   const [event, setEvent] = useState(null);

//   const [overview, setOverview] = useState({});

//   const [participants, setParticipants] = useState([]);
//   const [reminders, setReminders] = useState([]);

//   const [mom, setMom] = useState({
//     discussionPoints: "",
//     decisionsTaken: "",
//     actionItems: "",
//   });

//   useEffect(() => {
//     loadEvent();
//   }, []);

//   const loadEvent = async () => {
//     try {
//       const data = await fetchEventByCode(orgId, eventCode);

//       setEvent(data);

//       setOverview({
//         eventName: data.eventName,
//         eventType: data.eventType,
//         eventLocation: data.eventLocation,
//         organizerName: data.organizerName,
//         department: data.department,
//         eventDescription: data.eventDescription,
//       });
//     } catch (error) {
//       toast.error("Error loading event");
//     }
//   };

//   const handleOverviewSave = async () => {
//     try {
//       await updateEvent(eventCode, overview);

//       toast.success("Event Updated");
//     } catch (error) {
//       toast.error("Failed to update event");
//     }
//   };

//   const handleAddParticipant = async () => {
//     try {
//       const payload = participants.map((p) => ({
//         ...p,
//         createdBy: userDetails.userId,
//         organisationId: orgId,
//       }));

//       await addParticipants(eventCode, payload);

//       toast.success("Participants Added");
//     } catch (error) {
//       toast.error("Failed to add participants");
//     }
//   };

//   const handleAddReminder = async () => {
//     try {
//       const payload = {
//         ...reminders,
//         createdBy: userDetails.userId,
//         organisationId: orgId,
//       };

//       await addReminder(eventCode, payload);

//       toast.success("Reminder Added");
//     } catch (error) {
//       toast.error("Failed to add reminder");
//     }
//   };

//   const handleSaveMoM = async () => {
//     try {
//       const payload = {
//         ...mom,
//         createdBy: userDetails.userId,
//         organisationId: orgId,
//       };

//       await addMoM(eventCode, payload);

//       toast.success("Minutes Of Meeting Saved");
//     } catch (error) {
//       toast.error("Failed to save MoM");
//     }
//   };

//   if (!event) return null;

//   return (
//     <div className="main-content expand">
//       <Container fluid>
//         <Card className="mb-3">
//           <Card.Body>
//             <Row>
//               <Col md={3}>
//                 <b>Event Code</b>
//                 <p>{event.eventCode}</p>
//               </Col>

//               <Col md={3}>
//                 <b>Date</b>
//                 <p>{moment(event.eventDate).format("DD-MM-YYYY")}</p>
//               </Col>

//               <Col md={3}>
//                 <b>Location</b>
//                 <p>{event.eventLocation}</p>
//               </Col>

//               <Col md={3}>
//                 <b>Organizer</b>
//                 <p>{event.organizerName}</p>
//               </Col>
//             </Row>
//           </Card.Body>
//         </Card>

//         <Tabs defaultActiveKey="overview">
//           {/* OVERVIEW TAB */}

//           <Tab eventKey="overview" title="Overview">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Row>
//                   <Col md={4}>
//                     <Form.Group>
//                       <Form.Label>Event Name</Form.Label>
//                       <Form.Control
//                         value={overview.eventName}
//                         onChange={(e) =>
//                           setOverview({
//                             ...overview,
//                             eventName: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>

//                   <Col md={4}>
//                     <Form.Group>
//                       <Form.Label>Event Type</Form.Label>
//                       <Form.Control
//                         value={overview.eventType}
//                         onChange={(e) =>
//                           setOverview({
//                             ...overview,
//                             eventType: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>

//                   <Col md={4}>
//                     <Form.Group>
//                       <Form.Label>Department</Form.Label>
//                       <Form.Control
//                         value={overview.department}
//                         onChange={(e) =>
//                           setOverview({
//                             ...overview,
//                             department: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>
//                 </Row>

//                 <Row className="mt-3">
//                   <Col md={12}>
//                     <Form.Group>
//                       <Form.Label>Description</Form.Label>
//                       <Form.Control
//                         as="textarea"
//                         rows={4}
//                         value={overview.eventDescription}
//                         onChange={(e) =>
//                           setOverview({
//                             ...overview,
//                             eventDescription: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>
//                 </Row>

//                 <Button className="mt-3" onClick={handleOverviewSave}>
//                   Save Overview
//                 </Button>
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* PARTICIPANTS */}

//           <Tab eventKey="participants" title="Participants">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Button
//                   onClick={() =>
//                     setParticipants([
//                       ...participants,
//                       { userId: "", participantRole: "" },
//                     ])
//                   }
//                 >
//                   Add Participant
//                 </Button>

//                 <Table className="mt-3">
//                   <thead>
//                     <tr>
//                       <th>User Id</th>
//                       <th>Role</th>
//                     </tr>
//                   </thead>

//                   <tbody>
//                     {participants.map((p, index) => (
//                       <tr key={index}>
//                         <td>
//                           <Form.Control
//                             onChange={(e) => {
//                               const updated = [...participants];
//                               updated[index].userId = e.target.value;
//                               setParticipants(updated);
//                             }}
//                           />
//                         </td>

//                         <td>
//                           <Form.Control
//                             onChange={(e) => {
//                               const updated = [...participants];
//                               updated[index].participantRole = e.target.value;
//                               setParticipants(updated);
//                             }}
//                           />
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </Table>

//                 <Button onClick={handleAddParticipant}>
//                   Save Participants
//                 </Button>
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* REMINDERS */}

//           <Tab eventKey="reminders" title="Reminders">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Form.Group>
//                   <Form.Label>Reminder Minutes</Form.Label>

//                   <Form.Control
//                     type="number"
//                     onChange={(e) =>
//                       setReminders({
//                         reminderMinutes: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 <Button className="mt-3" onClick={handleAddReminder}>
//                   Save Reminder
//                 </Button>
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* RECURRENCE */}

//           <Tab eventKey="recurrence" title="Recurrence">
//             <Card className="mt-3">
//               <Card.Body>
//                 <p>Recurrence setup UI can be added here.</p>
//               </Card.Body>
//             </Card>
//           </Tab>

//           {/* MOM */}

//           <Tab eventKey="mom" title="MoM">
//             <Card className="mt-3">
//               <Card.Body>
//                 <Form.Group>
//                   <Form.Label>Discussion Points</Form.Label>

//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     onChange={(e) =>
//                       setMom({
//                         ...mom,
//                         discussionPoints: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 <Form.Group className="mt-2">
//                   <Form.Label>Decisions Taken</Form.Label>

//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     onChange={(e) =>
//                       setMom({
//                         ...mom,
//                         decisionsTaken: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 <Form.Group className="mt-2">
//                   <Form.Label>Action Items</Form.Label>

//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     onChange={(e) =>
//                       setMom({
//                         ...mom,
//                         actionItems: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 <Button className="mt-3" onClick={handleSaveMoM}>
//                   Save MoM
//                 </Button>
//               </Card.Body>
//             </Card>
//           </Tab>
//         </Tabs>
//       </Container>
//     </div>
//   );
// };

// export default EditEvent;
